import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Images } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import BackButton from "../../../../components/buttons/back/BackButton";
import Button from "../../../../components/buttons/Button";
import AlbumThumbnailCard from "../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import CreatorEditAlbumForm from "../forms/CreatorEditAlbumForm";
import CreatorAlbumPhotosView from "./CreatorAlbumPhotosView";

import { useCreatorAlbums } from "../hooks/useAlbums";

import "./CreatorAlbumsView.css";

const GRID_CONFIG = {
  columns: 4,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

export default function CreatorAlbumsView({ username }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const albumId = searchParams.get("a");

  const { albums, loading, error, getCreatorAlbums, deleteCreatorAlbum } =
    useCreatorAlbums(username);

  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [deletingAlbumId, setDeletingAlbumId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const albumList = Array.isArray(albums) ? albums : [];

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error?.message || "Unable to load albums."
    : "";

  useEffect(() => {
    if (!username) return;

    void getCreatorAlbums();
  }, [username, refreshKey]);

  const handleAlbumClick = (selectedAlbumId) => {
    if (!selectedAlbumId) return;

    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.set("a", String(selectedAlbumId));
        return params;
      },
      { preventScrollReset: true },
    );
  };

  const handleBackToAlbums = () => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.delete("a");
        return params;
      },
      { preventScrollReset: true },
    );
  };

  const handleRefresh = () => {
    if (loading) return;

    setRefreshKey((previous) => previous + 1);
  };

  const handleDeleteAlbum = async (selectedAlbumId) => {
    if (!selectedAlbumId || deletingAlbumId !== null) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this album? This cannot be undone.",
    );

    if (!confirmed) return;

    setDeletingAlbumId(selectedAlbumId);

    try {
      const result = await deleteCreatorAlbum(selectedAlbumId);

      if (result === false || result?.success === false) {
        throw new Error("Unable to delete album.");
      }

      if (String(albumId) === String(selectedAlbumId)) {
        handleBackToAlbums();
      }

      setRefreshKey((previous) => previous + 1);
    } catch (err) {
      console.error("Unable to delete album:", err);
    } finally {
      setDeletingAlbumId(null);
    }
  };

  const renderAlbum = (album) => (
    <AlbumThumbnailCard
      key={album.albumId ?? album.albumTag}
      album={album}
      onClick={handleAlbumClick}
      showMenu
      disabled={deletingAlbumId !== null}
      menuOptions={[
        {
          label: "Edit Album",
          action: () => setSelectedAlbum(album),
        },
        {
          label: "Delete Album",
          action: () => handleDeleteAlbum(album.albumId),
        },
      ]}
    />
  );

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title={albumId ? "Album Photos" : `My Albums (${albumList.length})`}
          subTitle={
            albumId ? "Manage your album photos." : "Manage your photo albums."
          }
        >
          {albumId && <BackButton onClick={handleBackToAlbums} />}
        </ContentPanelHeader>

        {albumId ? (
          <CreatorAlbumPhotosView username={username} albumId={albumId} />
        ) : (
          <>
            {errorMessage && albumList.length > 0 && (
              <div className="creator-album-view__error" role="alert">
                {errorMessage}

                <Button
                  value="Try Again"
                  variant="primary-outlined"
                  onClick={handleRefresh}
                  disabled={loading}
                />
              </div>
            )}

            {loading && !albumList.length ? (
              <ContentGrid
                {...GRID_CONFIG}
                items={[]}
                loading
                skeletonCount={8}
                renderItem={() => null}
              />
            ) : !albumList.length ? (
              <EmptyState
                icon={<Images size={28} />}
                title={errorMessage ? "Unable to load albums" : "No albums yet"}
                description={
                  errorMessage ||
                  "Create your first album to share photos with your community."
                }
                action={
                  errorMessage ? (
                    <Button
                      value="Try Again"
                      variant="primary-outlined"
                      onClick={handleRefresh}
                      disabled={loading}
                    />
                  ) : null
                }
              />
            ) : (
              <ContentGrid
                {...GRID_CONFIG}
                items={albumList}
                renderItem={renderAlbum}
              />
            )}
          </>
        )}
      </ContentPanel>

      {selectedAlbum && (
        <CreatorEditAlbumForm
          username={username}
          album={selectedAlbum}
          onClose={() => setSelectedAlbum(null)}
          onRefresh={handleRefresh}
        />
      )}
    </ViewContainer>
  );
}
